import { useKeyActionsRegistry } from '@solinkcorp/solink-components';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@solinkcorp/solink-ui/components/Dialog';
import { Kbd, KbdGroup } from '@solinkcorp/solink-ui/components/ui/kbd';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { useEffect, useState } from 'react';
import useLocale from 'src/hooks/useLocale';
import { formatShortcutKeys } from './formatShortcutKeys';

interface KeyboardShortcutsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const shortcutKeyClassName =
  'h-8 min-w-8 rounded border border-border-accent bg-transparent px-1.5 text-sm text-text-accent';

function ShortcutKeys({ shortcut }: { shortcut: string | string[] }) {
  const { modifiers, key } = formatShortcutKeys(shortcut);

  return (
    <KbdGroup className="shrink-0">
      {modifiers.map((modifier, index) => (
        <span key={`${index}-${modifier}`} className="inline-flex items-center gap-1">
          <Kbd className={shortcutKeyClassName}>{modifier}</Kbd>
          <span className="text-sm text-text-secondary">+</span>
        </span>
      ))}
      <Kbd className={shortcutKeyClassName}>{key}</Kbd>
    </KbdGroup>
  );
}

function useVisibleKeyboardShortcuts() {
  const actionsRegistry = useKeyActionsRegistry();
  const [shortcuts, setShortcuts] = useState(actionsRegistry.getVisisbleKeyboardShortcuts());

  useEffect(() => {
    const listener = () => {
      setShortcuts(actionsRegistry.getVisisbleKeyboardShortcuts());
    };
    actionsRegistry.on('updated', listener);
    return () => void actionsRegistry.removeListener('updated', listener);
  }, [actionsRegistry]);

  return shortcuts;
}

function useShortcutHelpOpen() {
  const actionsRegistry = useKeyActionsRegistry();

  useEffect(() => {
    actionsRegistry.shortcutHelpVisible = true;
    return () => {
      actionsRegistry.shortcutHelpVisible = false;
    };
  }, [actionsRegistry]);
}

function KeyboardShortcutsList() {
  const shortcuts = useVisibleKeyboardShortcuts();
  useShortcutHelpOpen();

  return (
    <div className="flex max-h-[80vh] flex-col gap-4 overflow-y-auto">
      {Object.keys(shortcuts).map((groupName, index) => (
        <section key={groupName} className={cn(index > 0 && 'border-t border-border-muted pt-4')}>
          <h2 className="mb-3 text-center font-light text-text-secondary heading-md">
            {groupName}
          </h2>
          <ul className="flex flex-col gap-3">
            {shortcuts[groupName].map(({ info, disabled, shortcut }) => (
              <li
                key={`${groupName}-${info.title}`}
                className={cn('flex items-center justify-between gap-4', disabled && 'opacity-40')}
              >
                <span className="body-md">{info.title}</span>
                <ShortcutKeys shortcut={shortcut} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function KeyboardShortcutsDialogAspen({ isOpen, onClose }: KeyboardShortcutsDialogProps) {
  const locale = useLocale();

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent size="m" className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{locale.keyboard.keyboardShortcuts}</DialogTitle>
          <DialogDescription className="sr-only">
            {locale.keyboard.keyboardShortcuts}
          </DialogDescription>
        </DialogHeader>
        <KeyboardShortcutsList />
      </DialogContent>
    </Dialog>
  );
}
